import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useLocation, useNavigate} from "react-router";
import {useTranslation} from 'react-i18next';
import {AxiosError} from 'axios';
import {Location} from 'history';
import client from 'lib/CommonApi/client';
import stringUtils from 'common/stringUtils';
import {
	callAlertWeb,
	callNativeWeb,
	clickSecurityKeypadEventWeb,
	footerShowEventWeb,
	hideProgressWeb,
	showProgressWeb
} from 'common/appInterface';
import CardApplyStopModal from 'components/CardApplyStopModal';

const CustomerInfoPage: React.FC = () => {
	/** Location State */
	const location = useLocation() as Location<LocationState>;
	/** Navigate */
	const navigate = useNavigate();
	/** 다국어처리 */
	const {t, i18n} = useTranslation();
	
	const screenId = "APPL10";
	const screenName = t("APPL10.title");
	
	const [cardApply, setCardApply] = useState<Partial<CardApplyStep5>>({
		...location.state.apply,
		proposerId: stringUtils.nvlDefault(location.state.query.inviterCode),
		userEngName: location.state.apply.userEngName?.replace(/[^A-Za-z\s]/ig, '').toUpperCase()
	});
	const [branchList, setBranchList] = useState<ManagementBranch[]>([]);
	const [passwordInput, setPasswordInput] = useState("");
	const [passwordConfirm, setPasswordConfirm] = useState("");
	const [passwordCheckCode, setPasswordCheckCode] = useState("");
	const [isBranchPopup, setBranchPopup] = useState(false);
	const [isClick, setClick] = useState(false);
	
	const passwordRef = useRef<HTMLParagraphElement>(null);
	const passwordConfirmRef = useRef<HTMLParagraphElement>(null);
	
	const [isStop, setStop] = useState(false);
	const closeStop = useCallback(() => setStop(false), []);
	
	/** 뒤로가기 처리 (backKeyEvent_App (APP > WEB)) */
	const callbackBackkeyEvent = useCallback(() => {
		if (isBranchPopup) {
			setBranchPopup(false);
		} else {
			setStop(true);
		}
	}, [isBranchPopup]);
	
	/** 보안키패드 입력요청 (clickSecurityKeypadEvent_Web (WEB > APP)) */
	const clickSecurityKeypadEvent = useCallback((type: string, title: string) => {
		clickSecurityKeypadEventWeb(screenId, screenName, type, "4", title);
	}, [screenName]);
	
	/** 보안키패드 입력 (typingSecurityKeypad_App (APP > WEB)) */
	const callbackTypingSecurityKeypadApp = useCallback((event) => {
		if (event.detail.keypadCode === "2") {
			setPasswordInput(Array<string>(Number.parseInt(event.detail.length)).fill("*").join(""));
		} else if (event.detail.keypadCode === "5") {
			setPasswordConfirm(Array<string>(Number.parseInt(event.detail.length)).fill("*").join(""));
		}
	}, []);
	
	/** 보안키패드 입력완료 처리 (completedPincode_App (APP > WEB)) */
	const callbackCompletedSecurityKeypadApp = useCallback((event) => {
		// 하단바 숨기기
		footerShowEventWeb("N");
		
		let length = event.detail.length === "0" ? 4 : Number.parseInt(event.detail.length);
		
		if (event.detail.keypadCode === "2") {
			setPasswordInput(Array<string>(length).fill("*").join(""));
			passwordRef.current?.classList.remove("error");
			setCardApply({
				...cardApply,
				passwordKeypadPid: event.detail.keypadPid,
				passwordEnteredValue: event.detail.enteredValue
			})
			
			client.post("/api/app/manage/password/check", {
				...location.state.query,
				passwordKeypadPid: event.detail.keypadPid,
				passwordEnteredValue: event.detail.enteredValue
			}).then((response) => {
					if (response.data.code === "00" || response.data.code === "0000") {
						setPasswordCheckCode(response.data.response.checkResult);
					}
				}
			)
		} else if (event.detail.keypadCode === "5") {
			setPasswordConfirm(Array<string>(length).fill("*").join(""));
			passwordConfirmRef.current?.classList.remove("error");
			setCardApply({
				...cardApply,
				confirmKeypadPid: event.detail.keypadPid,
				confirmEnteredValue: event.detail.enteredValue
			})
		}
	}, [location.state.query, cardApply]);
	
	/** 관리영업점 조회 */
	const callBranchList = useCallback(async () => {
		await client.get("/api/app/apply/branch", {params: location.state.query}).then((response) => {
			let result = response.data.response;
			setBranchList(result);
		}).catch((e: AxiosError) => {
			callAlertWeb("ERROR", "99", e.message);
		});
	}, [location]);
	
	/** Next 처리 */
	const clickNext = useCallback(() => {
		if (isClick) {
			console.log("Already Clicked...");
			return;
		}
		
		setClick(true);
		showProgressWeb();
		
		passwordRef.current?.classList.remove("error");
		passwordConfirmRef.current?.classList.remove("error");
		
		// 패스워드 검증
		client.patch("/api/app/apply/password/verify", {
			...location.state.query,
			receiveSeq: cardApply.receiveSeq,
			receiveDate: cardApply.receiveDate,
			passwordKeypadPid: cardApply.passwordKeypadPid,
			passwordEnteredValue: cardApply.passwordEnteredValue,
			confirmKeypadPid: cardApply.confirmKeypadPid,
			confirmEnteredValue: cardApply.confirmEnteredValue
		}).then((response) => {
			console.log(response);
			if (response.data.code === "00") {
				// 카드신청내역 등록
				client.patch("/api/app/apply/history", {
					...location.state.query,
					receiveSeq: cardApply.receiveSeq,
					receiveDate: cardApply.receiveDate,
					receiveStatusCode: "06",
					userEngName: cardApply.userEngName,
					managementBranchCode: cardApply.managementBranchCode,
					proposerId: cardApply.proposerId,
					cardProductCode: cardApply.cardProductCode
				}).then(() => {
					navigate("/apply/step6/method", {
						state: {
							...location.state,
							apply: {...cardApply, isChangeDelivery: false}
						}
					});
				}).catch((e: AxiosError) => {
					callAlertWeb("ERROR", "99", e.message);
				}).finally(() => {
					hideProgressWeb();
					setClick(false);
				});
			} else {
				hideProgressWeb();
				setClick(false);
				
				if (response.data.code === "91") {
					passwordRef.current?.classList.add("error");
				} else if (response.data.code === "92") {
					passwordConfirmRef.current?.classList.add("error");
				} else {
					callAlertWeb("ERROR", response.data.code, response.data.message);
				}
			}
		}).catch((e: AxiosError) => {
			hideProgressWeb();
			setClick(false);
			callAlertWeb("ERROR", "99", e.message);
		});
	}, [isClick, location.state, cardApply, navigate]);
	
	/** 영문성명 검증 */
	/*const checkUserName = useCallback((userEngName: string) => {
	  let regExp = /[^A-Za-z\s]/ig;
  
	  if (regExp.test(userEngName)) {
		callAlertWeb("ERROR", "ERROR.userName", t("ERROR.userName"));
	  }
  
	  setCardApply((value) => ({ ...value, userEngName: userEngName.replace(regExp, '').toUpperCase() }));
	}, [t]);*/
	
	// TODO TEST입력
	const testCall = (type: string) => {
		let keypadPid = "TEST_PID";
		let enteredValue = "TEST_ENTER_VALUE";
		
		if (type === "2") {
			keypadPid = "C1DD17010D5940E587861BDDDDAF544A";
			enteredValue = "c4ae4a276119237d1e0bf4793c7c473cbb20b93bae9b65ebd58f191c5f33e6ecd7cb716efee09270cf61fc08b594bc15a9d9766a4edff52e62f39335e197954e63e16656246a7b5de30457093d885277e601d799";
		} else if (type === "5") {
			keypadPid = "3EDCA9F3EE1A404C8AB6DE78E984AA81";
			enteredValue = "5e59b043e355e71bb7d8a46a26a39dc8ae755d7c017e59276744aa8b09fe76d23b8153f6d004e234a2010d1c0596434ed4755e972727ae34cfc16b07382577c23012de7f9d5a1d7b6d0fe4433b05199832a969e4";
		}
		
		let event = new CustomEvent("completKeypad", {
			detail: {
				keypadPid: keypadPid,
				keypadCode: type,
				enteredValue: enteredValue,
				length: "4"
			}
		});
		document.dispatchEvent(event);
	}
	
	// 최초실행
	useEffect(() => {
		// 언어설정
		i18n.changeLanguage(location.state.query.languageCode);
		// callNative_Web 처리 (WEB > APP)
		callNativeWeb(screenId, screenName);
		// 하단바 숨기기
		footerShowEventWeb("N");
		
		console.log("QUERY STATE");
		console.log(location.state);
		console.log(cardApply);
		
		callBranchList();
		
		// 최초한번실행 useEffect() Warning 제거
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
	
	// 뒤로가기 이벤트 리스너 (backKeyEvent_App (APP > WEB))
	useEffect(() => {
		document.addEventListener("backpress", callbackBackkeyEvent);
		return () => document.removeEventListener("backpress", callbackBackkeyEvent);
	}, [callbackBackkeyEvent]);
	
	// 보안키패드 입력 이벤트 리스너 (typingSecurityKeypad_App (APP > WEB))
	useEffect(() => {
		document.addEventListener("typingKeypad", callbackTypingSecurityKeypadApp);
		return () => document.removeEventListener("typingKeypad", callbackTypingSecurityKeypadApp);
	}, [callbackTypingSecurityKeypadApp]);
	
	// 보안키패드 결과전달 이벤트 리스너 (completedSecurityKeypad_App (APP > WEB))
	useEffect(() => {
		document.addEventListener("completKeypad", callbackCompletedSecurityKeypadApp);
		return () => document.removeEventListener("completKeypad", callbackCompletedSecurityKeypadApp);
	}, [callbackCompletedSecurityKeypadApp]);

	const handleProposerIdChange = (value:string) => {
		if (!/^[ABUabu]/.test(value)) {
			setCardApply((prevState) => ({
				...prevState,
				proposerId: "",
			}));
			return;
		}

		let filteredValue = value.slice(1).replace(/[^0-9]/g, ""); // 숫자 외 제거

		// 숫자가 7자리를 초과하면 잘라냄
		if (filteredValue.length > 7) {
			filteredValue = filteredValue.slice(0, 7);
		}

		setCardApply((prevState) => ({
			...prevState,
			proposerId: value[0] + filteredValue,
		}));
	};

	return (
		<section className="pdb70">
			<div id="wrap">
				{!isBranchPopup &&
                    <div id="container" className="pcHidden">
                        <section className="contents">
                            <div className="singUpWrapSub mgt20">
								{/* English Full Name */}
                                <div className="inputBoxTy01">
                                    <label>
                                        <input placeholder=" " type="text" readOnly
                                               value={cardApply.userEngName}
											// onChange={(e) => checkUserName(e.target.value)}
                                        />
                                        <span>{t("APPL10.header01")}</span>
                                        <p className="message">{t("APPL10.explan01")}</p>
                                    </label>
                                </div>
								{/* Card Password */}
                                <div className="inputBoxTy01">
                                    <label onClick={() => clickSecurityKeypadEvent('2', t('APPL10.header04'))}>
                                        <input placeholder=" " type="password" value={passwordInput} maxLength={4}
                                               readOnly className="inputReadOnly"
                                        />
                                        <span>{t('APPL10.header04')}</span>
                                        <p className="message" ref={passwordRef}
                                           onClick={e => e.stopPropagation()}
                                        >
											{t('APPL10.explan04')}
                                        </p>
                                        <div className={'rounded mgt8'}>
                                            <div className={'onLine'}>
												{
													(passwordCheckCode !== '') &&
                                                    <img className={'circle'} alt=""
                                                         src={passwordCheckCode === '10' || passwordCheckCode === '11' ? '/assets/images/ico/icon-pw-error.png' : '/assets/images/ico/icon_pw_check.png'}
                                                    />
												}
												{
													(passwordCheckCode === '') &&
                                                    <img className={'circle'} alt=""
                                                         src="/assets/images/ico/icon_pw_check_empty.png"
                                                    />
												}
                                                <p className={'message mgl5'}
                                                   style={passwordCheckCode !== '' ? {color: "#111111"} : {}}
                                                >
													{t('APPL10.explan05.01', 'Do not contain consecutive or identical numbers(1111, 1234)')}
                                                </p>
                                            </div>
                                            <div className={'onLine mgb5'}>
												{
													(passwordCheckCode !== "") &&
                                                    <img className={'circle'} alt=""
                                                         src={passwordCheckCode === '01' || passwordCheckCode === '11' ? '/assets/images/ico/icon-pw-error.png' : '/assets/images/ico/icon_pw_check.png'}
                                                    />
												}
												{
													(passwordCheckCode === "" || passwordCheckCode === "91") &&
                                                    <img className={'circle'} alt=""
                                                         src="/assets/images/ico/icon_pw_check_empty.png"
                                                    />
												}
                                                <p className={'message mgl5'}
                                                   style={passwordCheckCode !== '' ? {color: "#111111"} : {}}
                                                >
													{t('APPL10.explan05.02', 'Do not contain ID Number, Date of Birth, Phone Number, Card Number')}
                                                </p>
                                            </div>
                                        </div>
                                    </label>
                                </div>
								{/* Re-enter Card Password */}
                                <div className="inputBoxTy01">
                                    <label onClick={() => clickSecurityKeypadEvent("5", t("APPL10.header05"))}>
                                        <input placeholder=" " type="password" value={passwordConfirm} maxLength={4}
                                               readOnly className="inputReadOnly"
                                        />
                                        <span>{t("APPL10.header05")}</span>
                                        <p className="message" ref={passwordConfirmRef}
                                           onClick={e => e.stopPropagation()}
                                        >
											{t("APPL10.explan05")}
                                        </p>
                                    </label>
                                </div>
								{/* Invite Code */}
                                <div className="inputBoxTy01">
                                    <label>
                                        <input placeholder=" " type="text" value={cardApply.proposerId}
																							 onChange={(e) => handleProposerIdChange(e.target.value)}
                                        />
                                        <span>{t("APPL10.header03")}</span>
                                        <p className="message">{t("APPL10.explan03")}</p>
                                    </label>
                                </div>
								{/* Branch Invitation */}
                                <div className="inputBoxTy01">
                                    <label onClick={() => setBranchPopup(true)}>
                                        <input placeholder=" " type="text"
                                               value={stringUtils.checkValueNull(cardApply.managementBranchCode) ? "" : t(`BRANCH.name.${cardApply.managementBranchCode}`).toString()}
                                               readOnly className="inputReadOnly"
                                        />
                                        <span>{t("APPL10.header02")}</span>
                                        <p className="message"
                                           onClick={e => e.stopPropagation()}
                                        >
											{t("APPL10.explan02")}
                                        </p>
                                    </label>
                                    <button type="button" className="icoDown btnSearch"
                                            onClick={() => setBranchPopup(true)}
                                    >
                                        <span className="blind">선택</span>
                                    </button>
                                </div>
                            </div>
							{process.env.REACT_APP_PROFILE !== "prod" &&
                                <>
                                    <button className="btnPrimaryTy01" onClick={() => testCall("2")}>
                                        <span>카드비밀번호 입력</span>
                                    </button>
                                    <button className="btnPrimaryTy01" onClick={() => testCall("5")}>
                                        <span>카드비밀번호 확인 입력</span>
                                    </button>
                                </>
							}
                        </section>
                        <div className="btnArea">
                            <div className="btnWrapTy01">
                                <button type="button" className="btnPrimaryTy01"
                                        disabled={stringUtils.checkValueNull(cardApply.userEngName) || stringUtils.checkValueNull(cardApply.passwordEnteredValue) || stringUtils.checkValueNull(cardApply.confirmEnteredValue) || passwordCheckCode !== '00'}
                                        onClick={clickNext}
                                >
                                    <span>{t("COMMON.button.next")}</span>
                                </button>
                            </div>
                        </div>
                    </div>
				}
				{isBranchPopup &&
                    <div id="container" className="pcHidden">
                        <section className="contents listtyp01">
                            <div className="inner">
                                <section className="manage">
                                    <p className="tit">{t("APPL10.explan02")}</p>
									{branchList.map((value) =>
										<div
											className={cardApply.managementBranchCode === value.code ? "listbox on" : "listbox"}
											key={value.code}
										>
											<div className="radioBox">
												<input type="radio" id={`address_${value.code}`} name="address"
													   value={value.code}
													   onChange={(e) => setCardApply((value) => ({
														   ...value,
														   managementBranchCode: e.target.value
													   }))}
													   checked={cardApply.managementBranchCode === value.code}
												/>
												<label htmlFor={`address_${value.code}`}>
													<span><p>{t(`BRANCH.name.${value.code}`)}</p></span>
												</label>
											</div>
										</div>
									)}
                                    <div className={stringUtils.checkValueNull(cardApply.managementBranchCode) ? "listbox on" : "listbox"}>
                                        <div className="radioBox">
                                            <input type="radio" id="address_null" name="address" value={""}
                                                   onChange={(e) => setCardApply((value) => ({
													   ...value,
													   managementBranchCode: e.target.value
												   }))}
                                                   checked={stringUtils.checkValueNull(cardApply.managementBranchCode)}
                                            />
                                            <label htmlFor="address_null">
                                                <span><p>{t("APPL10.explan02_1")}</p></span>
                                            </label>
                                        </div>
                                    </div>
                                </section>
                            </div>
                        </section>
                        <div className="btnArea">
                            <div className="btnWrapTy01">
                                <button type="button" className="btnPrimaryTy01" disabled={isClick}
                                        onClick={() => setBranchPopup(false)}
								>
									<span>{t("COMMON.button.next")}</span>
                                </button>
                            </div>
                        </div>
                    </div>
				}
				<CardApplyStopModal isStop={isStop} closeStop={closeStop}/>
			</div>
		</section>
	);
};

export default CustomerInfoPage;